/**
 * «Twilio ya deja mandar: reanudá la marca». Cuando un envío dio 20003 con el token bueno, la marca queda frenada
 * por `cumplimiento` (falta Trust Hub); si después una prueba del Sandbox sale, Twilio ya no frena los envíos. Antes
 * nada lo decía: el diagnóstico seguía en rojo y el operador tenía que adivinar que podía reanudar.
 *
 * Lo ofrecen la prueba del Sandbox, la tarjeta de la marca frenada y el paso de cumplimiento del diagnóstico. No
 * reanuda solo: abre el modal con el motivo ya cargado (se puede cambiar) y pide «Reanudar la marca» condicionado al
 * freno que vio la pantalla (`siFrenadaAt`). Si en el medio apareció otro freno (un 20003 nuevo después de la prueba)
 * el backend no levanta (409 FRENO_CAMBIO): se dice por qué y se relee la cuenta.
 *
 * Va siempre montado donde se ofrece (con `superado` null no pinta nada): así el «por qué no» y el «listo» se siguen
 * viendo después de releer la cuenta, cuando el freno superado ya no está.
 */

import { useState } from "react";
import { CheckCircle2, PlayCircle } from "lucide-react";
import type { FrenoSuperado } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { TEXTO_FRENO_CAMBIO, TITULO_FRENO_SUPERADO, motivoReanudarTrasPrueba, textoFrenoSuperado } from "../../logic/conexion";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { ModalMotivo } from "../ModalMotivo";

export const TEXTO_REANUDADA_TRAS_PRUEBA = "Listo: la marca quedó reanudada. Las campañas frenadas no salen solas: reanudalas una por una.";

export function ReanudarTrasPrueba({ superado, soloBoton = false, alTerminar }: {
  superado: FrenoSuperado | null;
  /** sin el texto (el paso del diagnóstico ya lo dice en su detalle) */
  soloBoton?: boolean;
  /** después de reanudar o de que el backend diga que el freno cambió (el diagnóstico se vuelve a pedir) */
  alTerminar?: () => void;
}) {
  const { recargar } = useCuentaOficial();
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  // el freno y la prueba con los que se abrió el modal: lo que se manda aunque la cuenta cambie mientras tanto
  const [elegido, setElegido] = useState<FrenoSuperado | null>(null);

  const abrir = () => {
    if (!superado) return;
    setError("");
    setListo(false);
    setElegido(superado);
    setAbierto(true);
  };

  const reanudar = async (motivo: string) => {
    if (!elegido || ocupado) return;
    setOcupado(true);
    setError("");
    try {
      const l = leerRespuesta(await oficialApi.reanudarMarca(motivo, { siFrenadaAt: elegido.frenadaAt }));
      setAbierto(false);
      if (!l.ok) { setError(textoError(l)); return; }
      setListo(true);
      await recargar();
      alTerminar?.();
    } catch (e) {
      const x = leerError(e);
      // El modal se cierra siempre: el error se lee en la pantalla, al lado del botón (detrás del modal no se ve).
      setAbierto(false);
      if (x.code === "FRENO_CAMBIO") {
        // No levantó: el freno de ahora es otro (o ya no está). Se dice por qué y se muestra el de ahora.
        setError(x.error.trim() || TEXTO_FRENO_CAMBIO);
        await recargar();
        alTerminar?.();
        return;
      }
      setError(textoError(x));
    } finally {
      setOcupado(false);
    }
  };

  if (!superado && !error && !listo) return null;

  return (
    <div className="space-y-2">
      {superado && !soloBoton && (
        <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 space-y-1">
          <p className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5">
            <CheckCircle2 size={16} aria-hidden="true" /> {TITULO_FRENO_SUPERADO}
          </p>
          <p className="text-sm text-emerald-800">{textoFrenoSuperado(superado)}</p>
        </div>
      )}
      {superado && (
        <button type="button" onClick={abrir} disabled={ocupado}
          className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
          <PlayCircle size={15} aria-hidden="true" /> Reanudar la marca
        </button>
      )}
      {error && <p role="alert" className="text-sm text-red-700 font-medium">{error}</p>}
      {listo && !superado && <p role="status" className="text-sm text-emerald-700">{TEXTO_REANUDADA_TRAS_PRUEBA}</p>}
      {abierto && elegido && (
        <ModalMotivo
          titulo="Reanudar la marca"
          descripcion="La prueba del Sandbox salió después del freno. El motivo ya está cargado con la prueba: cambialo si querés. Las campañas frenadas no se reanudan solas: hay que reanudarlas una por una."
          confirmar="Reanudar"
          motivoInicial={motivoReanudarTrasPrueba(elegido)}
          onConfirmar={(m) => void reanudar(m)}
          onCancelar={() => setAbierto(false)}
          busy={ocupado}
        />
      )}
    </div>
  );
}

export default ReanudarTrasPrueba;
